/**
 * The passport publish-status vocabulary and transition graph for the browser —
 * the mirror of App\Enums\PublishStatus, which is the authority. Held together
 * by tests/fixtures/publish-status.json (see tests/js/publishStatus.test.js).
 *
 * Every status is explicit. Ontology-version status is a different
 * vocabulary — it stays `published`.
 */
export const PUBLISH_STATUSES = ['draft', 'released', 'revising', 'archived'];

export const PUBLISH_STATUS_LABELS = {
    draft: 'Draft',
    released: 'Released',
    revising: 'Revising',
    archived: 'Archived',
};

export const TRANSITIONS = {
    draft: ['released', 'archived'],
    released: ['revising', 'archived'],
    revising: ['released', 'archived'],
    archived: [],
};

/**
 * The statuses a move can reach: every one but draft, which nothing returns
 * to. What a status may be set to (a Lab bulk edit); filters keep all four.
 */
export const REACHABLE_STATUSES = PUBLISH_STATUSES.filter(
    (status) => Object.values(TRANSITIONS).some((targets) => targets.includes(status)),
);

/** The statuses whose values, friendly id and children may be written. */
export const EDITABLE = ['draft', 'revising'];

export function targetsFrom(from) {
    return TRANSITIONS[from] ?? [];
}

export function canTransition(from, to) {
    return targetsFrom(from).includes(to);
}

/** A stored status as a status: absent (pre-migration) reads as draft. */
export function normalizeStatus(stored) {
    return stored === null || stored === undefined || stored === '' ? 'draft' : stored;
}

/**
 * Why nothing may be created under a passport — no child, import or data
 * request — in this stored status: archived is final, and the server refuses
 * (`parent_archived`). Null for every other status; absent reads as draft.
 */
export const ARCHIVED_CREATE_REASON = 'Archived passports are final; nothing can be created under them';

export function createBlockedReason(stored) {
    return normalizeStatus(stored) === 'archived' ? ARCHIVED_CREATE_REASON : null;
}

/** Whether a passport in this stored status may be edited; absent reads as draft. */
export function isEditable(stored) {
    return EDITABLE.includes(normalizeStatus(stored));
}
